import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { LeadParagraph } from '../../components/LeadParagraph';
export const _frontmatter = {
  "pageTitle": "Modal API",
  "title": "Modal",
  "props": ["ModalProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const Link = makeShortcode("Link");
const ModalProps = makeShortcode("ModalProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

<PageContent componentName="modal" type="api" mdxType="PageContent">
<LeadParagraph mdxType="LeadParagraph">
  Modals inform users about a task and can contain critical information, require
  decisions, or involve multiple tasks.
</LeadParagraph>
<h2>{`Basic Usage`}</h2>
<p>{`The `}<inlineCode parentName="p">{`Modal`}</inlineCode>{` component renders its `}<inlineCode parentName="p">{`children`}</inlineCode>{` node in front of a backdrop component. It also disables scrolling of the page content while open,
properly manages focus; moving to the modal content, and keeping it there until the modal is closed, and adds the appropriate ARIA roles automatically.`}</p>
<p>{`The modal will `}<em parentName="p">{`only`}</em>{` render if the `}<inlineCode parentName="p">{`isOpen`}</inlineCode>{` prop is set to true. For that reason, it is redundant to show the modal using the following anti-pattern: `}<inlineCode parentName="p">{`{flag && <Modal isOpen={flag} />`}</inlineCode>{`. Instead,
simply use `}<inlineCode parentName="p">{`<Modal isOpen={flag} />`}</inlineCode></p>
<p>{`Although not required, it is helpful to inform users (especially screen reader users) when a button or link will trigger a modal dialog.
This can be done by supplementing the button label or link text with "(opens modal dialog)" using the `}<Link to="/api/visually-hidden/" mdxType="Link">{`VisuallyHidden`}</Link>{` component.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import {
  Button,
  Hyperlink,
  Modal,
  Paragraph,
  VisuallyHidden,
} from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const buttonRef = React.useRef();

  function handleOnClose() {
    setShowModal(false);
    buttonRef.current.focus();
  }

  return (
    <>
      <Modal header="Modal Title" onClose={handleOnClose} isOpen={showModal}>
        <Paragraph noMargins>This is a modal, doing modal things.</Paragraph>
        <Paragraph>
          This is <Hyperlink to="#">linked text</Hyperlink> in the modal
        </Paragraph>
        <Paragraph>
          <Button>This is a button</Button>
        </Paragraph>
        <Paragraph>
          This is <Hyperlink to="#">some more linked text</Hyperlink> in the
          modal
        </Paragraph>
      </Modal>
      <Button onClick={() => setShowModal(true)} ref={buttonRef}>
        Show Modal
        <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
      </Button>
    </>
  );
}
`}</code></pre>
<h2>{`Sizes`}</h2>
<p>{`Sizes for modals include `}<inlineCode parentName="p">{`small`}</inlineCode>{`, `}<inlineCode parentName="p">{`medium`}</inlineCode>{`, and `}<inlineCode parentName="p">{`large`}</inlineCode>{`, with `}<inlineCode parentName="p">{`medium`}</inlineCode>{` being the default value.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import {
  Button,
  ButtonSize,
  Modal,
  ModalSize,
  Paragraph,
  VisuallyHidden,
  ButtonGroup,
} from 'react-magma-dom';
export function Example() {
  const [showSmallModal, setShowSmallModal] = React.useState(false);
  const [showLargeModal, setShowLargeModal] = React.useState(false);
  const smallButtonRef = React.useRef();
  const largeButtonRef = React.useRef();

  return (
    <>
      <Modal
        size={ModalSize.small}
        header="Modal Small"
        onClose={() => {
          setShowSmallModal(false);
          smallButtonRef.current.focus();
        }}
        isOpen={showSmallModal}
      >
        <Paragraph noMargins>
          This is a small modal, doing small modal things.
        </Paragraph>
      </Modal>

      <Modal
        size={ModalSize.large}
        header="Modal Large"
        onClose={() => {
          setShowLargeModal(false);
          largeButtonRef.current.focus();
        }}
        isOpen={showLargeModal}
      >
        <Paragraph noMargins>
          This is a large modal, doing large modal things.
        </Paragraph>
      </Modal>

      <ButtonGroup>
        <Button
          size={ButtonSize.small}
          onClick={() => setShowSmallModal(true)}
          ref={smallButtonRef}
        >
          Show Small Modal
          <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
        </Button>
        <Button
          size={ButtonSize.large}
          onClick={() => setShowLargeModal(true)}
          ref={largeButtonRef}
        >
          Show Large Modal
          <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
        </Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
<h2>{`Modal Header`}</h2>
<p>{`The modal header prop is optional. It can accept a node or a string, and will be rendered inside an H1.
If there is a header passed in, the focus will be placed on the header when the modal opens. If not, the focus will be placed on
the first actionable element.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import {
  Button,
  Modal,
  ModalSize,
  Paragraph,
  VisuallyHidden,
  ButtonGroup,
  ButtonGroupAlignment,
} from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const [showModalHeader, setShowModalHeader] = React.useState(false);
  const buttonRef = React.useRef();
  const headerButtonRef = React.useRef();

  return (
    <>
      <Modal
        size={ModalSize.small}
        onClose={() => {
          setShowModal(false);
          buttonRef.current.focus();
        }}
        isOpen={showModal}
      >
        <Paragraph noTopMargin>This modal has no header.</Paragraph>
        <ButtonGroup alignment={ButtonGroupAlignment.center}>
          <Button onClick={() => setShowModal(false)}>OK</Button>
        </ButtonGroup>
      </Modal>
      <Modal
        header="This modal has a header"
        size={ModalSize.small}
        onClose={() => {
          setShowModalHeader(false);
          headerButtonRef.current.focus();
        }}
        isOpen={showModalHeader}
      >
        <Paragraph noTopMargin>This modal has no header.</Paragraph>
        <ButtonGroup alignment={ButtonGroupAlignment.center}>
          <Button onClick={() => setShowModalHeader(false)}>OK</Button>
        </ButtonGroup>
      </Modal>
      <ButtonGroup>
        <Button onClick={() => setShowModal(true)} ref={buttonRef}>
          Show Modal with no header
          <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
        </Button>
        <Button onClick={() => setShowModalHeader(true)} ref={headerButtonRef}>
          Show Modal with header
          <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
        </Button>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
<h2>{`Hide Close Button`}</h2>
<p>{`The close button can be hidden by using the `}<inlineCode parentName="p">{`isCloseButtonHidden`}</inlineCode>{` prop. If this prop is used, it is mandatory to provide another way to close the modal.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Button, Modal, Paragraph } from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const buttonRef = React.useRef();

  return (
    <>
      <Modal
        header="Modal Title"
        isCloseButtonHidden
        onClose={() => {
          setShowModal(false);
          buttonRef.current.focus();
        }}
        isOpen={showModal}
      >
        <Paragraph noTopMargin>
          The standard modal close button is hidden.
        </Paragraph>
        <Button onClick={() => setShowModal(false)}>Close this Dialog</Button>
      </Modal>
      <Button onClick={() => setShowModal(true)} ref={buttonRef}>
        Show Modal
      </Button>
    </>
  );
}
`}</code></pre>
<h2>{`Custom Close Button`}</h2>
<p>{`If you would like to add a custom close button to the `}<inlineCode parentName="p">{`Modal`}</inlineCode>{` be sure not to use the same `}<inlineCode parentName="p">{`onClose`}</inlineCode>{` function as it will mean that the function will be called twice (once internally and once by `}<inlineCode parentName="p">{`react-magma`}</inlineCode>{`).`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Button, Modal, Paragraph, VisuallyHidden } from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const [magmaCloseCalledTimes, setMagmaCloseCalledTimes] = React.useState(0);
  const [internalCloseCalledTimes, setInternalCloseCalledTimes] =
    React.useState(0);
  const buttonRef = React.useRef();

  function closeModal() {
    setMagmaCloseCalledTimes(magmaCloseCalledTimes + 1);
    setShowModal(false);
    buttonRef.current.focus();
  }

  function customCloseModal() {
    setInternalCloseCalledTimes(internalCloseCalledTimes + 1);
    setShowModal(false);
    buttonRef.current.focus();
  }

  return (
    <>
      <Modal header="Modal Title" onClose={closeModal} isOpen={showModal}>
        Lorem ipsum dolar sit amet
        <Button onClick={customCloseModal}>Close Modal</Button>
      </Modal>
      <Paragraph noMargins>
        <strong>Magma Close Called Times:</strong> {magmaCloseCalledTimes}
      </Paragraph>
      <Paragraph>
        <strong>Internal Close Called Times:</strong> {internalCloseCalledTimes}
      </Paragraph>
      <Button onClick={() => setShowModal(true)} ref={buttonRef}>
        Show Modal <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
      </Button>
    </>
  );
}
`}</code></pre>
<h2>{`Nested Modals`}</h2>
<p>{`Although we don't recommend using nested modals, the ability for one nested modal is supported. Take note of the DOM layout as two `}<inlineCode parentName="p">{`Modal`}</inlineCode>{`'s need to be siblings, otherwise mouse behavior will break.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import {
  Button,
  Modal,
  ModalSize,
  Paragraph,
  VisuallyHidden,
} from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const [showModal2, setShowModal2] = React.useState(false);

  return (
    <>
      <Modal
        header="Modal Title"
        onClose={() => {
          setShowModal(false);
        }}
        isOpen={showModal}
      >
        <Paragraph noTopMargin>This is a modal, doing modal things.</Paragraph>

        <Button onClick={() => setShowModal2(true)}>Show Modal 2</Button>
      </Modal>
      <Button onClick={() => setShowModal(true)}>
        Show Modal
        <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
      </Button>
      <Modal
        size={ModalSize.small}
        header="Modal 2 Title"
        onClose={() => setShowModal2(false)}
        isOpen={showModal2}
      >
        <Paragraph noMargins>This is modal 2</Paragraph>
      </Modal>
    </>
  );
}
`}</code></pre>
<h2>{`Close Modal With Confirmation`}</h2>
<p>{`Show confirmation modal when trying to close the main modal.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import {
  Button,
  ButtonGroup,
  Combobox,
  Modal,
  ModalSize,
  Paragraph,
  useFocusLock,
} from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>();
  const focusTrapElement = useFocusLock(!showConfirmationModal && showModal);

  const closeTheModal = () => {
    setShowConfirmationModal(true);
  };

  const closeTheConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const closeBothModals = () => {
    buttonRef.current.focus();
    setShowConfirmationModal(false);
    setShowModal(false);
  };

  return (
    <>
      <Button onClick={() => setShowModal(true)} ref={buttonRef}>
        Show Modal
      </Button>
      <Modal
        header="Modal Title"
        isModalClosingControlledManually
        onClose={closeTheModal}
        isOpen={showModal}
        ref={focusTrapElement}
      >
        <Paragraph noTopMargin>This is a modal, doing modal things.</Paragraph>
        <Paragraph>
          This is <a href="/">linked text</a> in the modal
        </Paragraph>
        <Combobox
          id="comboboxId3"
          isMulti
          labelText="Multi Combobox"
          defaultItems={[
            { label: 'Red', value: 'red' },
            { label: 'Blue', value: 'blue' },
            { label: 'Green', value: 'green' },
          ]}
          placeholder="Hello"
        />
      </Modal>
      <Modal
        size={ModalSize.small}
        header="Confirmation Modal"
        isModalClosingControlledManually
        onClose={closeTheConfirmationModal}
        isOpen={showConfirmationModal}
      >
        <Paragraph noTopMargin>Close the modal?</Paragraph>
        <ButtonGroup>
          <Button onClick={closeBothModals}>Yes</Button>
          <Button onClick={closeTheConfirmationModal}>No, go back</Button>
        </ButtonGroup>
      </Modal>
    </>
  );
}
`}</code></pre>
<h2>{`isInverse`}</h2>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import {
  Button,
  Hyperlink,
  Modal,
  Paragraph,
  VisuallyHidden,
  Card,
  CardBody,
} from 'react-magma-dom';
export function Example() {
  const [showModal, setShowModal] = React.useState(false);
  const buttonRef = React.useRef();

  return (
    <>
      <Modal
        header="Modal Title"
        onClose={() => {
          setShowModal(false);
          buttonRef.current.focus();
        }}
        isOpen={showModal}
        isInverse
      >
        <Paragraph isInverse noMargins>
          This is a modal, doing modal things.
        </Paragraph>
        <Paragraph isInverse>
          This is{' '}
          <Hyperlink to="#" isInverse>
            linked text
          </Hyperlink>{' '}
          in the modal
        </Paragraph>
        <Paragraph isInverse>
          <Button isInverse>This is a button</Button>
        </Paragraph>
        <Paragraph isInverse>
          This is{' '}
          <Hyperlink to="#" isInverse>
            some more linked text
          </Hyperlink>{' '}
          in the modal
        </Paragraph>
      </Modal>
      <Card isInverse>
        <CardBody>
          <Button onClick={() => setShowModal(true)} ref={buttonRef} isInverse>
            Show Modal
            <VisuallyHidden>(opens modal dialog)</VisuallyHidden>
          </Button>
        </CardBody>
      </Card>
    </>
  );
}
`}</code></pre>
<h2>{`Modal Props`}</h2>
<p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element that wraps the Modal content.`}</strong></p>
<p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes"
        }}>{`global HTML attributes`}</a>{` can be provided as props and will be applied to the `}<inlineCode parentName="p">{`div`}</inlineCode>{` element that wraps the modal content.`}</p>
<ModalProps mdxType="ModalProps" />
</PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      